<script lang="ts">
  // Detail for a family from a local source. It runs the same ExtractFamilyPackage the publish flow
  // uses to read geometry, parameters, category, units and the saved-in year straight from the .rfa,
  // shows a live 3D preview, and loads the family into the open project on demand — no download, since
  // the file is already on disk.

  import { onMount } from "svelte";
  import { transport } from "@/transport";
  import { categoryLabel, languageLabel } from "./categories";
  import FamilyViewer3D from "./FamilyViewer3D.svelte";
  import {
    getCachedLocalPackage,
    localPackageVersion,
    setCachedLocalMeta,
    setCachedLocalPackage,
  } from "./familyCache";
  import {
    AppBadge,
    AppButton,
    AppDialog,
    AppSelect,
    AppState,
  } from "@/components/ui";
  import type { FamilyPackage, LoadResult } from "./types";
  import { t, locale } from "@/i18n/index.svelte";
  import type { SupportedLocale } from "@/i18n/index.svelte";

  let {
    path,
    name,
    sizeBytes,
    modifiedTicks,
    onclose,
  }: {
    path: string;
    name: string;
    sizeBytes: number;
    modifiedTicks: number;
    onclose?: () => void;
  } = $props();

  let pkg = $state<FamilyPackage | null>(null);
  let status = $state<"loading" | "ready" | "error">("loading");
  let errorMessage = $state("");

  let action = $state<"idle" | "working" | "done" | "failed">("idle");
  let actionMessage = $state("");

  // Which type to place, and whether loading brings in the whole family or one type. The host has
  // always accepted a type name here — PlaceLocalFamily and LoadLocalFamily take one, and
  // FamilyLoader switches to LoadFamilySymbol when it is given — but nothing in this dialog ever
  // sent it, so a local family could only ever arrive whole. The cloud dialog asks the same two
  // questions the same way; a family from a folder is not a different kind of family.
  let selectedType = $state("");
  let loadMode = $state<"all" | "one">("all");
  let loadTypeName = $state("");

  // Only worth asking about when there is a choice to make.
  const hasTypeChoice = $derived((pkg?.types.length ?? 0) > 1);

  const category = $derived(
    pkg?.revitCategory
      ? categoryLabel(pkg.revitCategory, locale.value as SupportedLocale)
      : "—",
  );

  function kindClass(kind: string): string {
    if (kind === "builtin") return "text-success";
    if (kind === "shared") return "text-accent";
    return "text-ink-muted";
  }

  /** Both selects start on the family's first type, so neither can be submitted empty. */
  function primeTypeChoice(p: FamilyPackage) {
    selectedType ||= p.types[0] ?? "";
    loadTypeName ||= p.types[0] ?? "";
  }

  const modifiedDisplay = $derived.by(() => {
    if (!modifiedTicks || modifiedTicks <= 0) return "\u2014";
    const dt = new Date(modifiedTicks / 10_000 - 62_135_596_800_000);
    if (Number.isNaN(dt.getTime())) return "\u2014";
    return dt.toLocaleString();
  });

  onMount(() => {
    const version = localPackageVersion(sizeBytes, modifiedTicks);

    // Show what we already know, straight away. Reading a local family means opening it on the Revit
    // thread and tessellating it, which is seconds for anything complex — far too long to look at an
    // empty dialog for a preview the user has seen before. The entry is versioned by the file's size
    // and last-write time, so an edited family misses here and is read afresh.
    getCachedLocalPackage<FamilyPackage>(path, version).then((hit) => {
      if (hit && status === "loading") {
        pkg = hit;
        status = "ready";
        primeTypeChoice(hit);
      }
    });

    // Asked for regardless, and not only to refresh what is shown: the host keeps the full package —
    // mesh and thumbnail — for publishing to reuse, and that copy lives in the Revit session, not
    // here. Skipping this call on a cache hit would leave a card the user can look at but not
    // publish. It costs a dictionary lookup once the host has read this file in this session.
    transport
      .invoke<FamilyPackage>("ExtractFamilyPackage", { localPath: path })
      .then((res) => {
        if (!res.ok) {
          // A cached view is still a useful view; only an empty dialog needs to become an error.
          if (pkg) return;
          errorMessage = res.error ?? t("local.extractFailed");
          status = "error";
          return;
        }

        void setCachedLocalPackage(path, version, res);
        // The grid reads this one, not the package above — see familyCache for why they are separate.
        void setCachedLocalMeta(path, version, {
          revitYear: res.revitYear,
          unitSystem: res.unitSystem,
        });

        // Deliberately NOT reassigned when the cache already answered. The file has not changed —
        // the cache is versioned by its size and last-write time — so this package is the same data,
        // and swapping it in would hand the 3D viewer a new mesh object for identical geometry,
        // making it tear the model down and rebuild it while the user is already looking at it.
        if (pkg) return;

        pkg = res;
        status = "ready";
        primeTypeChoice(res);
      })
      .catch((error) => {
        if (pkg) return;
        errorMessage = error instanceof Error ? error.message : String(error);
        status = "error";
      });
  });

  /** Loads and hands over Revit's placement tool — the same primary action as the cloud catalog.
   *  Placing always brings the whole family in: the type choice here decides what gets drawn, not
   *  what reaches the project. */
  const placeInProject = () =>
    run("PlaceLocalFamily", selectedType || pkg?.types[0] || null);

  /** Loads without placing, for stocking a project up front — optionally just one type. */
  const loadIntoProject = () =>
    run(
      "LoadLocalFamily",
      hasTypeChoice && loadMode === "one"
        ? loadTypeName || pkg?.types[0] || null
        : null,
    );

  async function run(command: string, typeName: string | null) {
    action = "working";
    actionMessage = "";
    try {
      const result = await transport.invoke<LoadResult>(command, {
        path,
        typeName,
      });

      if (!result.loaded) {
        action = "failed";
        actionMessage = result.reason ?? t("local.loadFailed");
        return;
      }

      action = "done";

      // Same three outcomes as the catalog dialog: Revit is waiting for a click, the family is merely
      // loaded, or it was already there.
      if (result.placing) {
        actionMessage = result.placedTypeName
          ? t("detail.placingType", { type: result.placedTypeName })
          : t("detail.placing");
      } else if (result.reason) {
        actionMessage = result.reason;
      } else if (result.alreadyLoaded) {
        actionMessage = t("detail.alreadyLoaded");
      } else {
        actionMessage = t("local.loaded");
      }
    } catch (error) {
      action = "failed";
      actionMessage = error instanceof Error ? error.message : String(error);
    }
  }
</script>

<AppDialog
  size="sm"
  floatActions
  closeLabel={t("publish.close")}
  onclose={() => onclose?.()}
>
  {#snippet media()}
    {#if pkg}
      <div class="aspect-video bg-panel flex items-center justify-center">
        {#if pkg.mesh?.available}
          <FamilyViewer3D mesh={pkg.mesh} class="w-full h-full" />
        {:else}
          <span class="text-ink-muted text-sm">{t("viewer.noGeometry")}</span>
        {/if}
      </div>
    {/if}
  {/snippet}

  {#snippet header()}
    {#if pkg}
      <h2 class="text-lg font-semibold truncate">{name}</h2>
      <p class="text-sm text-ink-muted truncate">{category}</p>
    {/if}
  {/snippet}

  {#if status === "loading"}
    <AppState kind="loading">{t("local.reading")}</AppState>
  {:else if status === "error"}
    <AppState kind="error">{errorMessage}</AppState>
  {:else if pkg}
    <dl class="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1.5 text-sm">
      <dt class="text-ink-muted">{t("detail.language")}</dt>
      <dd>{languageLabel(pkg.suggestedLanguage)}</dd>
      <dt class="text-ink-muted">{t("detail.units")}</dt>
      <dd>{pkg.unitSystem ?? "—"}</dd>
      <dt class="text-ink-muted">{t("detail.revitYears")}</dt>
      <dd class="tabular-nums">{pkg.revitYear}</dd>
      <dt class="text-ink-muted">{t("detail.lastModified")}</dt>
      <dd>{modifiedDisplay}</dd>
    </dl>

    {#if pkg.types.length || pkg.parameters.length}
      <div class="flex flex-col gap-3">
        {#if pkg.types.length}
          <div>
            <p class="text-xs font-medium text-ink-muted mb-1">
              {t("detail.types")}
            </p>
            <div class="flex flex-wrap gap-1">
              {#each pkg.types as ty (ty)}
                <AppBadge tone="neutral">{ty}</AppBadge>
              {/each}
            </div>
          </div>
        {/if}
        {#if pkg.parameters.length}
          <div>
            <p class="text-xs font-medium text-ink-muted mb-1">
              {t("detail.parameters")} · {languageLabel(pkg.suggestedLanguage)}
            </p>
            <div class="border border-line rounded overflow-x-auto">
              <table class="w-full text-sm">
                <thead class="bg-panel text-ink-muted text-xs">
                  <tr>
                    <th class="text-left font-medium px-2 py-1.5">
                      {t("detail.parameterName")}
                    </th>
                    <th class="text-left font-medium px-2 py-1.5">
                      {t("detail.parameterType")}
                    </th>
                    <th class="text-left font-medium px-2 py-1.5">
                      {t("detail.value")}
                    </th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-line">
                  {#each pkg.parameters as p, i (i)}
                    <tr>
                      <td class="px-2 py-1.5 align-top">{p.rawName}</td>
                      <td class="px-2 py-1.5 align-top">
                        <span class="text-[10px] uppercase {kindClass(p.kind)}"
                          >{p.kind}</span
                        >
                        {#if p.isInstance}
                          <span
                            class="text-[10px] uppercase text-ink-muted ml-1"
                            >{t("detail.instance")}</span
                          >
                        {/if}
                      </td>
                      <td class="px-2 py-1.5 align-top text-ink-muted">
                        {#if !p.isFormulaDriven && p.value != null && p.value !== ""}
                          {p.value}
                        {:else}
                          —
                        {/if}
                      </td>
                    </tr>
                  {/each}
                </tbody>
              </table>
            </div>
          </div>
        {/if}
      </div>
    {/if}

    <div class="flex flex-col gap-2">
      <!-- Which size to place. Asked before the click rather than after, because the placement tool
           starts immediately and there is no moment afterwards to interrupt with a question. -->
      {#if hasTypeChoice}
        <label class="flex flex-col gap-1">
          <span class="text-xs font-medium text-ink-muted"
            >{t("detail.chooseType")}</span
          >
          <AppSelect bind:value={selectedType}>
            {#each pkg.types as ty (ty)}
              <option value={ty}>{ty}</option>
            {/each}
          </AppSelect>
        </label>
      {/if}

      <AppButton
        variant="primary"
        size="lg"
        block
        disabled={action === "working"}
        onclick={placeInProject}
      >
        {action === "working"
          ? t("detail.loading")
          : t("detail.placeInProject")}
      </AppButton>

      <!-- Load mode: every type, or one via LoadFamilySymbol. Scoped to the load button rather than
           the place button above — placing always loads the whole family, since its type choice is
           about what gets drawn, not what reaches the project. -->
      {#if hasTypeChoice}
        <div class="flex flex-col gap-1.5">
          <span class="text-xs font-medium text-ink-muted"
            >{t("detail.loadMode")}</span
          >
          <div class="flex items-center gap-4 text-sm">
            <label class="flex items-center gap-1.5">
              <input type="radio" value="all" bind:group={loadMode} />
              {t("detail.loadModeAll")}
            </label>
            <label class="flex items-center gap-1.5">
              <input type="radio" value="one" bind:group={loadMode} />
              {t("detail.loadModeOne")}
            </label>
          </div>
          {#if loadMode === "one"}
            <AppSelect bind:value={loadTypeName}>
              {#each pkg.types as ty (ty)}
                <option value={ty}>{ty}</option>
              {/each}
            </AppSelect>
          {/if}
        </div>
      {/if}

      <AppButton
        variant="secondary"
        size="md"
        block
        disabled={action === "working"}
        onclick={loadIntoProject}
      >
        {t("detail.loadIntoProject")}
      </AppButton>
      {#if actionMessage}
        <p
          class="text-xs text-center {action === 'failed'
            ? 'text-error'
            : 'text-ink-muted'}"
        >
          {actionMessage}
        </p>
      {/if}
    </div>
  {/if}
</AppDialog>
